import {
  DueDisplayOptions,
  ID,
  noDeadlineSectionID,
  overdueSectionID,
  PrecomputedCompletion,
  RelationshipUpdate,
  Task,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  getTaskPlanning,
  parseDateKey,
} from '@griply/utils';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { getTaskIdFromTreeId } from 'shared/utils/tree-id-helpers';

import { DeadlineHeader } from '../deadline-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './deadline-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export type MultipleDeadlinesGroupTaskListProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  weekStartsOn: WeekDays;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  completionData?: Record<string, PrecomputedCompletion>;
};

export const MultipleDeadlinesGroupTaskList: React.FC<
  MultipleDeadlinesGroupTaskListProps
> = ({
  treeData,
  tasks,
  onReorder,
  weekStartsOn,
  collapsedSections = [],
  onSectionCollapse,
  scrollElement,
  denseFormat,
  isContextMenuOpen,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
  completionData,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  // Only clone treeData when drag-reorder is meaningful (not on the
  // completed page where completionData is provided).
  const oldTreeData = useMemo(
    () => (completionData ? treeData : structuredClone(treeData)),
    [treeData, completionData],
  );

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        const taskId = getTaskIdFromTreeId(item.id);
        if (!tasksMap[taskId]) {
          return;
        }

        const task = tasksMap[taskId];
        const taskToUpdate = structuredClone(task);

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id;
        const oldSectionId = oldSectionItem.id;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        // Handle overdue section - tasks cannot be moved into overdue
        if (newSectionId === overdueSectionID) {
          return;
        }

        // Handle "No deadline" section - remove deadline
        if (newSectionId === noDeadlineSectionID) {
          // only remove deadline if task has no schedules
          if (!task.schedules?.length) {
            taskToUpdate.endStrategy = {
              deadline: null,
              completionCount: task.endStrategy?.completionCount ?? null,
            };
            updatedDocs.push(taskToUpdate);
          }
          return;
        }

        // Parse the new date from section ID
        const newDate = parseDateKey(newSectionId);
        const newDeadline = endOfDay(newDate);

        // Handle scheduled tasks - use mutation
        if (task.schedules?.length) {
          // Get the planned occurrence from the old section date
          const oldDate =
            oldSectionId === overdueSectionID
              ? endOfDay(new Date(0)) // Use epoch for overdue
              : parseDateKey(oldSectionId);

          const [plannedOccurrence] = getTaskPlanning(task, {
            startDate: startOfDay(oldDate),
            endDate: endOfDay(oldDate),
            today,
            weekStartsOn,
          });

          const schedules = createPlannedOccurrenceForDate(
            task.schedules,
            newDeadline,
            {
              timeslot: plannedOccurrence?.timeslot ?? null,
              originalDate: oldDate,
              today,
              weekStartsOn,
            },
          );

          updateTasks([
            {
              id: task.id,
              schedules,
            },
          ]);
          return;
        }

        // Handle non-scheduled tasks - update deadline via onReorder
        taskToUpdate.endStrategy = {
          deadline: newDeadline,
          completionCount: taskToUpdate.endStrategy?.completionCount ?? null,
        };
        taskToUpdate.schedules = null;
        updatedDocs.push(taskToUpdate);
        return;
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.Date) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const completionDataRef = useRef(completionData);
  completionDataRef.current = completionData;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;
          const parentElement = itemInstance.getParent();
          const parentId = parentElement?.getItemData().sourceId;
          const date = parentId ? parseDateKey(parentId) : undefined;
          const sectionDate = date && !isNaN(date.getTime()) ? date : undefined;

          // Hide due date unless it is the overdue section
          const effectiveDisplayDue =
            parentId === overdueSectionID ? displayDue : [];

          const taskId = getTaskIdFromTreeId(item.sourceId);

          return currentTasksMap[taskId] ? (
            <TreeTaskCard
              task={currentTasksMap[taskId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              completionDate={sectionDate}
              displayDate={sectionDate}
              displayDue={effectiveDisplayDue}
              showRepeatingAsTodo={showRepeatingAsTodo}
              precomputedCompletion={completionDataRef.current?.[item.sourceId]}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const dateStr = item.sourceId.replace('-new-task', '');
          const date =
            dateStr === noDeadlineSectionID
              ? undefined
              : dateStr === overdueSectionID
                ? endOfDay(addDays(today, -1))
                : parseDateKey(dateStr);
          return (
            <NewTaskRow
              date={date}
              startTime={null}
              duration={null}
              goal={goalId}
              lifeArea={lifeAreaId}
              tags={tagId ? [tagId] : undefined}
            />
          );
        }

        // Date
        if (type === TreeItemTypes.Date) {
          return (
            <DeadlineHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }

        return null;
      },
    [
      denseFormat,
      today,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
    ],
  );

  const [tree] = useTree();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
